import React from "react"
import {
    ModalBody,
    Tab,
    TabList,
    TabPanel,
    PenaltiesInfographic,
    PenaltiesInfographicStep,
    Chip,
    Modal,
    Tabs,
    ModalBodyContent,
    BlockContentValue,
} from "@silversea-ssc/ui-components-react"
import { t } from "@lingui/macro"
import { Option } from "fp-ts/Option"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { FareCode } from "@silversea-ssc/web-fares"
import { CruisePaymentTermsResponse } from "../../../../_common/dot-com-api-types"
import BlockContent from "../../../../../components/BlockContent/BlockContent"
import TextBox from "../../../../../components/TextBox/TextBox"
import {
    CancellationPolicyModalSectionContent,
    SanityPenaltiesModalContent,
} from "../../../../_common/sanity/sanity-types"
import { useECommerceMachine } from "../../../../_common/machines/machines-context"
import useModal from "../../../../../components/ModalProvider/useModal"
import { stringIsPresent } from "../../../../_common/checks"
import { maybePaymentTermsByFareCode } from "../../../_common/payment-terms"
import {
    firstPeriodIsTypeDeposit,
    formattedAdministrativeFeeDisclaimer,
    toPenaltiesInfographicStep,
} from "../../helpers"
import styles from "./FarePenaltiesModal.module.scss"

interface FarePenaltiesModalProps {
    showCruiseGroupDisclaimer?: boolean
    selectedFareCode: FareCode
    showOnlySelectedFareCode?: boolean
}

const FarePenaltiesModal: React.FC<React.PropsWithChildren<FarePenaltiesModalProps>> = ({
    showCruiseGroupDisclaimer,
    selectedFareCode,
    showOnlySelectedFareCode = false,
}) => {
    const [state] = useECommerceMachine()
    const { closeModal } = useModal()
    const { sanityContent, fareCards } = state.context
    const selectedIndex = fareCards.findIndex((x) => x.fare === selectedFareCode)

    const penaltiesContent = sanityContent.penaltiesModalContent

    if (!penaltiesContent) {
        // keep new line
        console.warn("No sanity penaltiesModalContent")
        return null
    }

    return (
        <Modal
            title={t`Cancellation Policy`}
            size="medium"
            modal={{ isOpen: true, onRequestClose: () => closeModal("closeWithCallback") }}
            dataContentName="fare_penalties_modal"
        >
            {showOnlySelectedFareCode ? (
                <ModalBody variant="full" paddingBottom paddingTop>
                    <ModalBodyContent>
                        <ContentForFare
                            paymentTerms={maybePaymentTermsByFareCode(state.context, selectedFareCode)}
                            penaltiesContent={penaltiesContent}
                            showCruiseGroupDisclaimer={showCruiseGroupDisclaimer}
                        />
                    </ModalBodyContent>
                </ModalBody>
            ) : (
                <ModalBody variant="full" paddingBottom paddingTop={false}>
                    <Tabs data-content-name="tabs" forceRenderTabPanel defaultIndex={selectedIndex}>
                        <TabList spacingTop>
                            {fareCards.map((fareCard) => {
                                return (
                                    <Tab key={fareCard.fare} underline data-content-piece={`tab-${fareCard.fare}`}>
                                        {fareCard.fareLabel}
                                    </Tab>
                                )
                            })}
                        </TabList>

                        {fareCards.map((fareCard) => {
                            const paymentTerms = maybePaymentTermsByFareCode(state.context, fareCard.fare)
                            return (
                                <TabPanel
                                    key={fareCard.fare}
                                    spacingTop
                                    data-content-piece={`tab-content-${fareCard.fare}`}
                                >
                                    <ModalBodyContent>
                                        <ContentForFare
                                            paymentTerms={paymentTerms}
                                            penaltiesContent={penaltiesContent}
                                            showCruiseGroupDisclaimer={showCruiseGroupDisclaimer}
                                        />
                                    </ModalBodyContent>
                                </TabPanel>
                            )
                        })}
                    </Tabs>
                </ModalBody>
            )}
        </Modal>
    )
}

const buildLabel = (content: CancellationPolicyModalSectionContent | undefined): JSX.Element => {
    const label = content?.staticInfographicRefundableLabel
    const chipContent = content?.staticInfographicFCCNote

    return (
        <React.Fragment>
            {label}
            {label && chipContent && <br />}
            {chipContent && (
                <Chip background="neutral05" className={styles.chipNote}>
                    {chipContent}
                </Chip>
            )}
        </React.Fragment>
    )
}

const ContentBox: React.FC<
    React.PropsWithChildren<{
        title: BlockContentValue
        content: BlockContentValue
        disclaimer?: string
    }>
> = ({ title, content, disclaimer, children }) => {
    return (
        <div className={styles.box}>
            <div className={styles.title}>
                <BlockContent value={title} />
            </div>
            <p>
                <TextBox content={content} fontSize="inherit" />
            </p>
            {children}
            {disclaimer && <p className={styles.disclaimer}>{disclaimer}</p>}
        </div>
    )
}

type ContentForFareProps = {
    paymentTerms: Option<CruisePaymentTermsResponse>
    penaltiesContent: SanityPenaltiesModalContent
    showCruiseGroupDisclaimer?: boolean
}
const ContentForFare: React.FC<ContentForFareProps> = ({
    penaltiesContent,
    paymentTerms,
    showCruiseGroupDisclaimer,
}) => {
    const maybePaymentTerms = O.toUndefined(paymentTerms)
    const cancellationAdminFee = F.pipe(
        paymentTerms,
        O.chain((paymentTerms) => paymentTerms.cancellationAdminFee),
        O.toUndefined,
    )

    const periods = F.pipe(
        O.Do,
        O.apS("paymentTerms", paymentTerms),
        O.bind("periods", ({ paymentTerms }) => paymentTerms.cancellationPeriods),
        O.map(({ paymentTerms, periods }) =>
            periods.map(toPenaltiesInfographicStep(paymentTerms.currency, cancellationAdminFee)),
        ),
        O.getOrElseW(() => []),
    )

    const administrativeFeeDisclaimer = firstPeriodIsTypeDeposit(paymentTerms)
        ? "*Incurs a A$500 administrative fee per person. The rest of your deposit will be converted in a Future Cruise Credit (FCC)"
        : formattedAdministrativeFeeDisclaimer(cancellationAdminFee, maybePaymentTerms?.currency)

    if (!penaltiesContent) {
        // keep new line
        console.warn("No sanity penaltiesModalContent")
        return null
    }

    const cancelledByGuestDueOtherReasons = penaltiesContent.cancelledBy?.find(
        (x) => x?.type == "by-guest-due-other-reasons",
    )

    const cancelledBySilverseaDueOtherReasons = penaltiesContent.cancelledBy?.find(
        (x) => x?.type == "by-silversea-due-other-reasons",
    )

    return (
        <React.Fragment>
            {stringIsPresent(cancelledByGuestDueOtherReasons?.title) && (
                <ContentBox
                    title={cancelledByGuestDueOtherReasons?.title}
                    content={cancelledByGuestDueOtherReasons?.content}
                    disclaimer={showCruiseGroupDisclaimer ? cancelledByGuestDueOtherReasons?.disclaimer : undefined}
                >
                    {periods && (
                        <PenaltiesInfographic
                            className={styles.infographic}
                            startLabel={t({
                                id: "Booking date",
                                message: "Booking date",
                            })}
                            endLabel={t({
                                id: "Departure date",
                                message: "Departure date",
                            })}
                            administrativeFeeDisclaimer={administrativeFeeDisclaimer}
                        >
                            {periods.map(({ key, ...step }) => {
                                return <PenaltiesInfographicStep key={key} {...step} compactOnMobile />
                            })}
                        </PenaltiesInfographic>
                    )}
                </ContentBox>
            )}

            {stringIsPresent(cancelledBySilverseaDueOtherReasons?.title) && (
                <ContentBox
                    title={cancelledBySilverseaDueOtherReasons?.title}
                    content={cancelledBySilverseaDueOtherReasons?.content}
                    disclaimer={showCruiseGroupDisclaimer ? cancelledBySilverseaDueOtherReasons?.disclaimer : undefined}
                >
                    <PenaltiesInfographic
                        className={styles.infographic}
                        startLabel={t({
                            id: "Booking date",
                            message: "Booking date",
                        })}
                        endLabel={t({
                            id: "Departure date",
                            message: "Departure date",
                        })}
                    >
                        <PenaltiesInfographicStep
                            label={buildLabel(cancelledBySilverseaDueOtherReasons)}
                            color="green"
                        />
                    </PenaltiesInfographic>
                </ContentBox>
            )}
        </React.Fragment>
    )
}

export default FarePenaltiesModal
